import { supabase } from "@/integrations/supabase/client";
import { Department } from "@/types/department";
import { TaskAssignee } from "@/types/kanban";

// Function to fetch departments with proper type annotations
export const fetchDepartments = async (): Promise<Department[]> => {
  try {
    const { data, error } = await supabase
      .from('departments')
      .select('*')
      .order('name');

    if (error) {
      console.error("Error fetching departments:", error);
      return [];
    }

    // Fetch goals for each department
    const departments = await Promise.all(
      (data || []).map(async (dept) => {
        // Get department goals
        const { data: goalsData, error: goalsError } = await supabase
          .from('department_goals')
          .select('goal')
          .eq('department_id', dept.id);
        
        if (goalsError) {
          console.error("Error fetching goals for department:", goalsError);
        }
        
        // Get employees (collaborators)
        const { data: employeesData, error: empError } = await supabase
          .from('employees')
          .select('id, name, avatar')
          .eq('department_id', dept.id)
          .limit(5);
        
        if (empError) {
          console.error("Error fetching employees for department:", empError);
        }
          
        return {
          id: dept.id,
          name: dept.name,
          description: dept.description || "",
          headName: dept.head_name,
          headTitle: dept.head_title,
          headAvatar: dept.head_avatar,
          budget: dept.budget,
          year: dept.year,
          mfoCount: dept.mfo_count,
          vision: dept.vision || "",
          mission: dept.mission || "",
          goals: goalsData?.map(g => g.goal) || [],
          collaborators: employeesData?.map(emp => ({
            id: emp.id,
            name: emp.name,
            avatar: emp.avatar
          })) || [],
          createdAt: dept.created_at,
          updatedAt: dept.updated_at,
        };
      })
    );

    return departments;
  } catch (error) {
    console.error("Unexpected error:", error);
    return [];
  }
};

export const fetchDepartmentById = async (id: string): Promise<Department | null> => {
  try {
    const { data, error } = await supabase
      .from("departments")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("Error fetching department by ID:", error);
      return null;
    }

    if (!data) return null;

    // Fetch department goals
    const { data: goalsData, error: goalsError } = await supabase
      .from('department_goals')
      .select('goal')
      .eq('department_id', id);

    if (goalsError) {
      console.error("Error fetching department goals:", goalsError);
    }

    // Fetch employees for collaborators
    const { data: employeesData, error: empError } = await supabase
      .from('employees')
      .select('id, name, avatar')
      .eq('department_id', id)
      .limit(5);

    if (empError) {
      console.error("Error fetching employees:", empError);
    }

    return {
      id: data.id,
      name: data.name,
      description: data.description || "",
      headName: data.head_name,
      headTitle: data.head_title,
      headAvatar: data.head_avatar,
      budget: data.budget,
      year: data.year,
      vision: data.vision || "",
      mission: data.mission || "",
      mfoCount: data.mfo_count,
      goals: goalsData?.map(g => g.goal) || [],
      collaborators: employeesData?.map(emp => ({
        id: emp.id,
        name: emp.name,
        avatar: emp.avatar
      })) || [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error("Error fetching department by ID:", error);
    return null;
  }
};

export const createDepartment = async (department: {
  name: string;
  description?: string;
  headName: string;
  headTitle: string;
  budget: number;
  vision?: string;
  mission?: string;
  goals?: string[];
}): Promise<Department | null> => {
  try {
    // First, create the department
    const { data, error } = await supabase
      .from("departments")
      .insert({
        name: department.name,
        description: department.description || "",
        head_name: department.headName,
        head_title: department.headTitle,
        budget: department.budget,
        vision: department.vision || "",
        mission: department.mission || ""
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating department:", error);
      return null;
    }

    // Then add any goals if they exist
    if (department.goals && department.goals.length > 0) {
      const goalsToInsert = department.goals
        .filter(goal => goal && goal.trim() !== "")
        .map(goal => ({
          department_id: data.id,
          goal: goal.trim()
        }));

      if (goalsToInsert.length > 0) {
        const { error: goalsError } = await supabase
          .from("department_goals")
          .insert(goalsToInsert);

        if (goalsError) {
          console.error("Error adding department goals:", goalsError);
        }
      }
    }

    // Create notification for department creation
    try {
      const { data: userData } = await supabase.auth.getSession();
      const userId = userData.session?.user.id;
      
      if (userId) {
        await supabase
          .from("notifications")
          .insert([
            {
              user_id: userId,
              title: "Division Created",
              message: `You created a new division: ${department.name}`,
              type: "department_created",
              is_read: false
            }
          ]);
      }
    } catch (notificationError) {
      console.error("Failed to create notification:", notificationError);
    }

    return {
      id: data.id,
      name: data.name,
      description: data.description || "",
      headName: data.head_name,
      headTitle: data.head_title,
      headAvatar: data.head_avatar,
      budget: data.budget,
      year: data.year,
      mission: data.mission || "",
      vision: data.vision || "",
      mfoCount: data.mfo_count || 0,
      goals: department.goals?.filter(goal => goal && goal.trim() !== "") || [],
      collaborators: [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error("Error creating department:", error);
    return null;
  }
};

export const updateDepartment = async (
  id: string,
  department: Partial<{
    name: string;
    description: string;
    headName: string;
    headTitle: string;
    headAvatar: string;
    budget: number;
    mission: string;
    vision: string;
    goals: string[];
  }>
): Promise<boolean> => {
  try {
    const updates: Record<string, any> = {};

    if (department.name) updates.name = department.name;
    if (department.description !== undefined) updates.description = department.description;
    if (department.headName) updates.head_name = department.headName;
    if (department.headTitle) updates.head_title = department.headTitle;
    if (department.headAvatar) updates.head_avatar = department.headAvatar;
    if (department.budget !== undefined) updates.budget = department.budget;
    if (department.mission !== undefined) updates.mission = department.mission;
    if (department.vision !== undefined) updates.vision = department.vision;

    const { error } = await supabase
      .from("departments")
      .update(updates)
      .eq("id", id);

    if (error) {
      console.error("Error updating department:", error);
      return false;
    }

    // Update goals if they're provided
    if (department.goals) {
      // First, delete existing goals
      const { error: deleteError } = await supabase
        .from("department_goals")
        .delete()
        .eq("department_id", id);

      if (deleteError) {
        console.error("Error deleting existing goals:", deleteError);
        return false;
      }

      // Then add the new goals
      const goalsToInsert = department.goals
        .filter(goal => goal && goal.trim() !== "")
        .map(goal => ({
          department_id: id,
          goal: goal.trim()
        }));

      if (goalsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("department_goals")
          .insert(goalsToInsert);

        if (insertError) {
          console.error("Error adding updated goals:", insertError);
          return false;
        }
      }
    }

    // Create notification for department update
    try {
      const { data: userData } = await supabase.auth.getSession();
      const userId = userData.session?.user.id;
      
      if (userId) {
        await supabase
          .from("notifications")
          .insert([
            {
              user_id: userId,
              title: "Division Updated",
              message: `You updated a division: ${department.name || ""}`,
              type: "department_updated",
              is_read: false
            }
          ]);
      }
    } catch (notificationError) {
      console.error("Failed to create notification:", notificationError);
    }

    return true;
  } catch (error) {
    console.error("Error updating department:", error);
    return false;
  }
};

export const deleteDepartment = async (id: string): Promise<boolean> => {
  try {
    // First, get the department name for the notification
    const { data: departmentData, error: fetchError } = await supabase
      .from("departments")
      .select("name")
      .eq("id", id)
      .maybeSingle();
      
    if (fetchError) {
      console.error("Error fetching department for deletion:", fetchError);
    }

    // Delete department goals first (handle foreign key constraints)
    const { error: goalsError } = await supabase
      .from("department_goals")
      .delete()
      .eq("department_id", id);
      
    if (goalsError) {
      console.error("Error deleting department goals:", goalsError);
    }

    // Then delete the department
    const { error } = await supabase
      .from("departments")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting department:", error);
      return false;
    }

    // Create notification for department deletion
    try {
      const { data: userData } = await supabase.auth.getSession();
      const userId = userData.session?.user.id;
      
      if (userId && departmentData) {
        await supabase
          .from("notifications")
          .insert([
            {
              user_id: userId,
              title: "Division Deleted",
              message: `You deleted division: ${departmentData.name}`,
              type: "department_deleted",
              is_read: false
            }
          ]);
      }
    } catch (notificationError) {
      console.error("Failed to create notification:", notificationError);
    }

    return true;
  } catch (error) {
    console.error("Error deleting department:", error);
    return false;
  }
};

export const getDepartmentById = async (id: string): Promise<Department | null> => {
  return fetchDepartmentById(id);
};
